import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for checking user permissions
 * 
 * Usage:
 * const { hasAccess, canView, canEdit, canDelete } = usePermission();
 * 
 * if (hasAccess('finance')) {
 *   // Show finance module
 * }
 * 
 * if (canEdit('products')) {
 *   // Show edit button
 * }
 */
export function usePermission() {
  const { permissions, role } = useAuth();

  // Permission level hierarchy
  const PERMISSION_LEVELS = {
    none: 0,
    view: 1,
    edit: 2,
    full: 3
  };

  const getPermissionLevel = (module) => {
    if (!permissions || !permissions[module]) return 'none';
    return permissions[module];
  };

  const checkPermission = (module, requiredLevel) => {
    const userLevel = getPermissionLevel(module);
    const userRank = PERMISSION_LEVELS[userLevel] || 0;
    const requiredRank = PERMISSION_LEVELS[requiredLevel] || 0;
    
    // Debug logging for finance module
    if (module === 'finance') {
      console.log('🔍 Checking finance permission:', {
        module,
        requiredLevel,
        userLevel,
        userRank,
        requiredRank,
        hasAccess: userRank >= requiredRank,
        allPermissions: permissions
      });
    }
    
    return userRank >= requiredRank;
  };

  return {
    // Check if user has any access to module
    hasAccess: (module) => checkPermission(module, 'view'),
    
    // Check if user can view module
    canView: (module) => checkPermission(module, 'view'),
    
    // Check if user can edit in module
    canEdit: (module) => checkPermission(module, 'edit'),
    
    // Check if user has full access (can delete)
    canDelete: (module) => checkPermission(module, 'full'),
    
    // Get raw permission level
    getPermission: (module) => getPermissionLevel(module),
    
    // Check if user is Owner or Admin
    isOwnerOrAdmin: () => role && ['Owner', 'Admin'].includes(role.role_name),
    
    // Check if user is Owner
    isOwner: () => role && role.role_name === 'Owner',
    
    // Get all permissions
    permissions
  };
}
